/**
 * UNDO-TRASH-LIGHT (L1) — glue connecting undo stack to the UI.
 *
 * A single stack per UI session (module-scoped, like toasts store):
 *   - `registerUndo(...)` called by domain mutations after writing:
 *     pushes inverse and displays coalesced pill "done · Undo".
 *   - `undoLastAction()` (pill button or Ctrl/Cmd+Z) applies inverse via
 *     normal write path and confirms with "Undone" flash.
 *   - `resetUndoScope(userId)` empties stack when active account changes:
 *     inverses capture DB/session they were created with and must NEVER
 *     cross accounts.
 *
 * COPYABLE piece across apps: only depends on agnostic shared module and
 * local toast system.
 */

import { createUndoStack } from "@habits/shared";
import { dismissUndoFlash, showSavedFlash, showToast, showUndoFlash } from "@/components/ui/toast";

const undoStack = createUndoStack();

/** Pre-translated texts accompanying an undo (stack is i18n-agnostic). */
export interface UndoMessages {
  /** What was just done, e.g. "Asset deleted". */
  done: string;
  /** Button label, e.g. "Undo". */
  undoLabel: string;
  /** Confirmation after undoing, e.g. "Undone". */
  undone: string;
  /** Error if inverse fails, e.g. "Couldn't undo". */
  failed: string;
}

/**
 * Registers inverse of newly applied mutation and displays undoable
 * pill. `inverse` must re-enter via normal write path (PowerSync/Drizzle
 * mutations), never touch in-memory state directly.
 */
export function registerUndo(messages: UndoMessages, inverse: () => Promise<void>): void {
  undoStack.push({
    label: messages.done,
    undo: async () => {
      try {
        await inverse();
        showSavedFlash(messages.undone);
      } catch (err: unknown) {
        console.error("[undo]", err instanceof Error ? err.message : String(err));
        showToast(messages.failed, "error");
        throw err;
      }
    },
  });
  showUndoFlash(messages.done, {
    label: messages.undoLabel,
    onAction: () => {
      void undoLastAction();
    },
  });
}

/** Undoes last action (pill or Ctrl/Cmd+Z shortcut). Does not throw. */
export async function undoLastAction(): Promise<void> {
  dismissUndoFlash();
  // Feedback (success/error) is already emitted by each entry wrapper.
  await undoStack.undoLast().catch(() => undefined);
}

export function undoStackSize(): number {
  return undoStack.size();
}

let undoScopeUserId: string | undefined;

/**
 * Sets "owner" of stack. If active account changes (multi-account,
 * login/logout, guest→user), stack empties and pill disappears.
 */
export function resetUndoScope(userId: string | undefined): void {
  if (userId === undoScopeUserId) {
    return;
  }
  undoScopeUserId = userId;
  undoStack.clear();
  dismissUndoFlash();
}
